import { writable, get } from 'svelte/store';
import type { Pope, PopeRaw, ProgressData, PopeProgress } from '$lib/types';

// Store for pope data
export const popes = writable<Pope[]>([]);
export const popesLoaded = writable(false);
export const popesError = writable<string | null>(null);

// Format date for display (e.g., "26 May 0189")
function formatDateString(date: Date): string {
  const day = date.getDate();
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];
  const month = months[date.getMonth()];
  const year = date.getFullYear().toString().padStart(4, '0');
  return `${day} ${month} ${year}`;
}

// Calculate difference in days between two dates
function getDaysBetween(start: Date, end: Date): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((end.getTime() - start.getTime()) / msPerDay);
}

// Transform raw JSON data to normalized format
function transformPope(raw: PopeRaw): Pope {
  const papacies = [];
  
  // Create a single papacy entry from the pontification dates
  if (raw.PontificateStart && raw.PontificateEnd) {
    const startDate = new Date(raw.PontificateStart);
    const endDate = new Date(raw.PontificateEnd);
    papacies.push({
      isAntipapacy: raw.AntipopeNumber > 0 && raw.PopeNumber === 0,
      startYear: startDate.getFullYear(),
      endYear: endDate.getFullYear(),
      startMonth: startDate.getMonth() + 1,
      endMonth: endDate.getMonth() + 1,
      startDay: startDate.getDate(),
      endDay: endDate.getDate(),
      ordinal: raw.AntipopeNumber > 0 ? raw.AntipopeNumber : raw.PopeNumber,
      lengthInDays: getDaysBetween(startDate, endDate),
      startDateString: formatDateString(startDate),
      endDateString: formatDateString(endDate)
    });
  }
  
  return {
    personId: raw.PersonId,
    popeNumber: raw.PopeNumber,
    antipopeNumber: raw.AntipopeNumber,
    englishName: raw.EnglishName,
    latinName: raw.LatinName,
    wikipageUrl: raw.WikipageUrl,
    wikipediaExtract: raw.WikipediaExtract,
    yearsAtDeath: raw.YearsAtDeath,
    monthsAtDeath: raw.MonthsAtDeath,
    portraitFile: raw.PortraitFile,
    badgeFile: raw.BadgeFile,
    papacies
  };
}

// Load popes from local public folder
export async function loadPopes(): Promise<void> {
  if (get(popesLoaded)) return;
  
  try {
    const response = await fetch('/data/popes.json');
    if (!response.ok) throw new Error('Failed to fetch popes data');
    const rawData: PopeRaw[] = await response.json();
    // Filter out interregnums (periods without pope - both PopeNumber and AntipopeNumber are 0)
    const filteredData = rawData.filter(p => p.PopeNumber !== 0 || p.AntipopeNumber !== 0);
    const normalizedData = filteredData.map(transformPope);
    popes.set(normalizedData);
    popesLoaded.set(true);
  } catch (error) {
    popesError.set(error instanceof Error ? error.message : 'Unknown error');
  }
}

// Helper functions
function getTotalMonthsAtDeath(pope: Pope): number {
  return pope.yearsAtDeath * 12 + pope.monthsAtDeath;
}

function hasBeenPope(pope: Pope): boolean {
  return pope.popeNumber > 0;
}

function hasBeenAntipope(pope: Pope): boolean {
  return pope.antipopeNumber > 0;
}

function getDifference(pope: Pope, months: number, abbreviate: boolean): string {
  const popeMonths = getTotalMonthsAtDeath(pope);
  const diff = Math.abs(months - popeMonths);

  if (abbreviate) {
    if (diff === 0) return 'last mo';
    const timeRange = diff < 12 ? `${diff}mo` : `${Math.floor(diff / 12)}y`;
    return popeMonths <= months ? `« ${timeRange}` : `${timeRange} »`;
  } else {
    if (diff === 0) return 'less than a month';
    const years = Math.floor(diff / 12);
    const remainingMonths = diff % 12;
    let result = '';
    if (years > 0) result += `${years} year${years === 1 ? '' : 's'} `;
    if (remainingMonths > 0) result += `${remainingMonths} month${remainingMonths === 1 ? '' : 's'}`;
    return result.trim();
  }
}

// Calculate progress for a given birth year/month
export function calculateProgress(birthYear: number, birthMonth: number): ProgressData | null {
  const popeList = get(popes);
  if (popeList.length === 0) return null;

  const now = new Date();
  const totalMonths = (now.getFullYear() * 12 + now.getMonth() + 1) - (birthYear * 12 + birthMonth);

  if (totalMonths < 0) return null;

  const ageYears = Math.floor(totalMonths / 12);
  const ageMonths = totalMonths % 12;
  const age = ageMonths > 0
    ? `${ageYears} year${ageYears === 1 ? '' : 's'} ${ageMonths} month${ageMonths === 1 ? '' : 's'}`
    : `${ageYears} year${ageYears === 1 ? '' : 's'}`;

  // Count outlived
  let outlivedPopes = 0;
  let outlivedAntipopes = 0;
  let totalPopesCount = 0;
  let totalAntipopesCount = 0;

  for (const pope of popeList) {
    const isPureAntipope = hasBeenAntipope(pope) && !hasBeenPope(pope);
    const popeMonths = getTotalMonthsAtDeath(pope);

    if (isPureAntipope) {
      totalAntipopesCount++;
      if (popeMonths < totalMonths) outlivedAntipopes++;
    } else {
      totalPopesCount++;
      if (popeMonths < totalMonths) outlivedPopes++;
    }
  }

  const popesToGo = totalPopesCount - outlivedPopes;
  const antipopesToGo = totalAntipopesCount - outlivedAntipopes;
  const rank = popesToGo + antipopesToGo + 1;
  const maxRank = popeList.length + 1;
  const progress = Math.floor(100 * (outlivedPopes + outlivedAntipopes) / popeList.length);

  // Find popes outlived this exact month
  const lastMonthPopes = popeList.filter(p => {
    const isPureAntipope = hasBeenAntipope(p) && !hasBeenPope(p);
    return !isPureAntipope && getTotalMonthsAtDeath(p) === totalMonths;
  });
  const lastMonthAntipopes = popeList.filter(p => {
    const isPureAntipope = hasBeenAntipope(p) && !hasBeenPope(p);
    return isPureAntipope && getTotalMonthsAtDeath(p) === totalMonths;
  });

  let lastMonthNotification: string | null = null;
  if (lastMonthPopes.length > 0 || lastMonthAntipopes.length > 0) {
    const parts: string[] = [];
    if (lastMonthPopes.length > 0) {
      parts.push(`${lastMonthPopes.length} pope${lastMonthPopes.length === 1 ? '' : 's'}`);
    }
    if (lastMonthAntipopes.length > 0) {
      parts.push(`${lastMonthAntipopes.length} antipope${lastMonthAntipopes.length === 1 ? '' : 's'}`);
    }
    lastMonthNotification = parts.join(' and ');
  }

  // Find preceding (outlived, closest to current age)
  const preceding = popeList
    .filter(p => getTotalMonthsAtDeath(p) < totalMonths)
    .sort((a, b) => getTotalMonthsAtDeath(b) - getTotalMonthsAtDeath(a))
    .slice(0, 4);

  // Find following (not yet outlived, closest to current age)
  const following = popeList
    .filter(p => getTotalMonthsAtDeath(p) >= totalMonths)
    .sort((a, b) => getTotalMonthsAtDeath(a) - getTotalMonthsAtDeath(b))
    .slice(0, 4);

  // Previous (for details section)
  const previous = lastMonthNotification
    ? [...lastMonthPopes, ...lastMonthAntipopes]
    : preceding.slice(0, 1);

  const convertToProgress = (pope: Pope, idx: number, isPreceding: boolean, count: number): PopeProgress => ({
    pope,
    portraitFile: `/assets/images/popes/portraits/${pope.portraitFile}`,
    badgeFile: `/assets/images/popes/badges/${pope.badgeFile}`,
    hasBeenAntipope: hasBeenAntipope(pope),
    hasBeenPope: hasBeenPope(pope),
    englishName: pope.englishName,
    difference: getDifference(pope, totalMonths, true),
    className: `fade${isPreceding ? count - idx - 1 : idx}`,
    rank: rank + (isPreceding ? count - idx : -1 - idx)
  });

  return {
    year: birthYear,
    month: birthMonth,
    age,
    totalMonths,
    outlivedPopes,
    outlivedAntipopes,
    popesToGo,
    antipopesToGo,
    totalPopes: totalPopesCount,
    totalAntipopes: totalAntipopesCount,
    progress,
    rank,
    maxRank,
    showLastMonthNotification: lastMonthNotification !== null,
    lastMonthNotification,
    preceding: preceding.map((p, i) => convertToProgress(p, i, true, preceding.length)),
    following: following.map((p, i) => convertToProgress(p, i, false, following.length)),
    previous: previous.map((p, i) => ({
      ...convertToProgress(p, i, true, previous.length),
      difference: getDifference(p, totalMonths, false)
    }))
  };
}
